// ============================================================
// Sync-Engine — zentrale DB mit Offline-Sync
// Design: docs/ZENTRALE-DB-PLAN.md
//
// Ablauf: lokale Schreiboperationen werden per Dexie-DBCore-Middleware
// in die sync_outbox gespiegelt (dirty-Tracking). syncNow() pusht die
// Outbox zum Server (Last-Write-Wins entscheidet dort) und pullt danach
// alle Server-Änderungen seit dem letzten Wasserzeichen.
// Trigger: 60s-Intervall, online-Event, debounced nach Schreibop.
// ============================================================

import Dexie from 'dexie';
import { db, type SyncOutboxEntry } from '../db/schema';
import {
  loadSalt, storeSalt, encryptString, decryptToString,
  uint8ToBase64, base64ToUint8,
} from './crypto';
import { SessionKeyStore } from './keyStore';

// ── Konfiguration ──────────────────────────────────────────────────────────

const DEFAULT_SERVER = 'https://amed.dr-karrer.com';
const SYNC_INTERVAL_MS = 60_000;
const WRITE_DEBOUNCE_MS = 3_000;

/** Synchronisierte Tabellen (müssen der Server-Registry entsprechen). */
const SYNCED_TABLES = new Set([
  'mandanten', 'arbeitsplaetze', 'mitarbeiter', 'vorsorge',
  'honorarvereinbarungen', 'leistungserfassung', 'rechnungen',
  'mahnungen', 'zeiteintraege', 'learn_patterns', 'einstellungen',
  'akte', 'begehungen', 'erinnerungen', 'protokolle',
]);

/** einstellungen: nur geteilte Praxis-Keys syncen, Geräte-Token bleiben lokal.
 *  key_check = mit dem Master-Key verschlüsselter Kontrollwert; erlaubt
 *  Zweitgeräten, ein falsches Master-Passwort SOFORT zu erkennen. */
const SYNC_SETTINGS_KEYS = new Set(['aussteller', 'key_check']);

function pkOf(table: string): string {
  return table === 'einstellungen' ? 'key' : 'id';
}

// ── Meta-Zugriff ───────────────────────────────────────────────────────────

async function getMeta(key: string): Promise<string | null> {
  const row = await db.sync_meta.get(key);
  return row?.value ?? null;
}

async function setMeta(key: string, value: string): Promise<void> {
  await db.sync_meta.put({ key, value });
}

export async function getServerUrl(): Promise<string> {
  return (await getMeta('server_url')) ?? DEFAULT_SERVER;
}

export async function setServerUrl(url: string): Promise<void> {
  await setMeta('server_url', url.replace(/\/+$/, ''));
}

export async function isSyncConfigured(): Promise<boolean> {
  return (await getMeta('token')) !== null;
}

async function clientId(): Promise<string> {
  let id = await getMeta('client_id');
  if (id === null) {
    id = crypto.randomUUID();
    await setMeta('client_id', id);
  }
  return id;
}

// ── API-Client (Bearer-Token; Cookies scheitern an Cross-Site) ────────────

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const base = await getServerUrl();
  const token = await getMeta('token');
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token !== null ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  // Auth-Status merken: 401 mit vorhandenem Token = abgelaufene Sitzung.
  if (res.status === 401 && token !== null) authExpired = true;
  else if (res.ok) authExpired = false;
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
    const msg = typeof body?.error === 'string' ? body.error : `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

// ── Login / Logout ─────────────────────────────────────────────────────────

export async function syncLogin(email: string, password: string): Promise<void> {
  const { token } = await api<{ token: string }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  await setMeta('token', token);
  await setMeta('email', email);
  authExpired = false;
}

/** Bootstrap: legt das (einzige) Server-Konto an und meldet direkt an. */
export async function syncRegister(email: string, password: string): Promise<void> {
  const { token } = await api<{ token: string }>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  await setMeta('token', token);
  await setMeta('email', email);
}

export async function syncLogout(): Promise<void> {
  await db.sync_meta.delete('token');
  authExpired = false;
  emitStatus();
}

export async function getSyncAccount(): Promise<string | null> {
  return (await getMeta('token')) !== null ? getMeta('email') : null;
}

// ── Multi-Device: Salt teilen + Master-Passwort-Kontrolle ─────────────────

const KEY_CHECK_PLAINTEXT = 'AMED-KEY-CHECK-V1';

/** Lädt das lokale Salt zum Server hoch (idempotent; Server ist immutabel). */
export async function uploadSalt(): Promise<void> {
  const salt = await loadSalt();
  if (salt === null) return;
  await api('/api/crypto/salt', {
    method: 'PUT',
    body: JSON.stringify({ salt: uint8ToBase64(salt) }),
  });
}

/** Holt das Salt vom Server und speichert es lokal (Gerät-B-Onboarding). */
export async function adoptRemoteSalt(): Promise<void> {
  const { salt } = await api<{ salt: string }>('/api/crypto/salt');
  await storeSalt(base64ToUint8(salt));
}

/** Legt den key_check an, falls er fehlt (braucht aktive Session). */
export async function ensureKeyCheck(): Promise<void> {
  if (!SessionKeyStore.isActive()) return;
  const existing = await db.einstellungen.get('key_check');
  if (existing !== undefined) return;
  const blob = await encryptString(KEY_CHECK_PLAINTEXT);
  await db.einstellungen.put({ key: 'key_check', value: uint8ToBase64(blob) });
}

/** Prüft nach dem Pull, ob der lokale Master-Key zu den Server-Daten passt.
 *  true = passt (oder kein key_check vorhanden → nichts prüfbar). */
export async function verifyKeyCheck(): Promise<boolean> {
  const row = await db.einstellungen.get('key_check');
  if (row === undefined) return true;
  try {
    return (await decryptToString(base64ToUint8(row.value))) === KEY_CHECK_PLAINTEXT;
  } catch {
    return false;
  }
}

// ── Dirty-Tracking (DBCore-Middleware) ─────────────────────────────────────

let middlewareRegistered = false;

/** VOR db.open() aufrufen (geschieht in bootstrap.safeOpenDB). */
export function registerSyncMiddleware(): void {
  if (middlewareRegistered) return;
  middlewareRegistered = true;

  db.use({
    stack: 'dbcore',
    name: 'amedSyncTracking',
    create(downlevel) {
      return {
        ...downlevel,
        table(name) {
          const t = downlevel.table(name);
          if (!SYNCED_TABLES.has(name)) return t;
          return {
            ...t,
            mutate: async (req) => {
              const res = await t.mutate(req);
              /* Pull-Apply nicht zurück in die Outbox spiegeln. */
              const trans = Dexie.currentTransaction as unknown as
                { __syncApply?: boolean } | null;
              if (trans?.__syncApply === true) return res;

              const now = new Date().toISOString();
              const pk = pkOf(name);
              const entries: SyncOutboxEntry[] = [];

              if (req.type === 'add' || req.type === 'put') {
                for (const v of req.values as Array<Record<string, unknown>>) {
                  const id = v[pk];
                  if (typeof id !== 'string') continue;
                  if (name === 'einstellungen' && !SYNC_SETTINGS_KEYS.has(id)) continue;
                  entries.push({
                    key: `${name}:${id}`, table: name, row_id: id,
                    deleted: false, modified_at: now,
                  });
                }
              } else if (req.type === 'delete') {
                for (const k of req.keys as unknown[]) {
                  if (typeof k !== 'string') continue;
                  if (name === 'einstellungen' && !SYNC_SETTINGS_KEYS.has(k)) continue;
                  entries.push({
                    key: `${name}:${k}`, table: name, row_id: k,
                    deleted: true, modified_at: now,
                  });
                }
              }
              /* deleteRange (table.clear) wird bewusst nicht getrackt —
               * kommt nur beim Backup-Restore vor; danach hilft ein
               * manueller Voll-Push (queueFullPush). */

              if (entries.length > 0) {
                /* Per setTimeout(0) aus der Dexie-Transaktionszone lösen —
                 * die Outbox ist nicht Teil des laufenden Table-Locks
                 * (Dexie.ignoreTransaction scheitert bei Auto-Transaktionen
                 * an fehlendem transless-PSD). Verlust bei Crash in dieser
                 * Millisekunde ist verschmerzbar; queueFullPush heilt. */
                setTimeout(() => {
                  void db.sync_outbox.bulkPut(entries).catch((e: unknown) => {
                    console.error('[Sync] Outbox-Schreibfehler:', e);
                  });
                  scheduleDebouncedSync();
                }, 0);
              }
              return res;
            },
          };
        },
      };
    },
  });
}

// ── Push ───────────────────────────────────────────────────────────────────

interface PushRow {
  base_rev: number;
  client_modified_at: string;
  deleted: boolean;
  data: Record<string, unknown>;
}

async function pushOutbox(): Promise<{ pushed: number; rejected: number }> {
  const entries = await db.sync_outbox.toArray();
  if (entries.length === 0) return { pushed: 0, rejected: 0 };

  const byTable = new Map<string, Array<{ entry: SyncOutboxEntry; row: PushRow }>>();

  for (const entry of entries) {
    const revRow = await db.sync_revs.get(entry.key);
    const baseRev = revRow?.rev ?? 0;

    if (entry.deleted) {
      /* Nie gesyncte Zeile lokal gelöscht → am Server nichts zu tun. */
      if (baseRev === 0) { await db.sync_outbox.delete(entry.key); continue; }
      const list = byTable.get(entry.table) ?? [];
      list.push({
        entry,
        row: {
          base_rev: baseRev, client_modified_at: entry.modified_at,
          deleted: true, data: { [pkOf(entry.table)]: entry.row_id },
        },
      });
      byTable.set(entry.table, list);
      continue;
    }

    const row = (await db.table(entry.table).get(entry.row_id)) as
      Record<string, unknown> | undefined;
    if (row === undefined) { await db.sync_outbox.delete(entry.key); continue; }

    const list = byTable.get(entry.table) ?? [];
    list.push({
      entry,
      row: {
        base_rev: baseRev, client_modified_at: entry.modified_at,
        deleted: false, data: row,
      },
    });
    byTable.set(entry.table, list);
  }

  if (byTable.size === 0) return { pushed: 0, rejected: 0 };

  /* In Häppchen senden — Akte-Einträge können Foto-Anhänge (Base64)
   * tragen; alles in einem Request würde das Body-Limit sprengen. */
  const CHUNK = 15;
  const flat = [...byTable.entries()].flatMap(([table, list]) =>
    list.map((x) => ({ table, ...x })),
  );
  const cid = await clientId();
  let pushed = 0;
  let rejectedCount = 0;

  for (let i = 0; i < flat.length; i += CHUNK) {
    const slice = flat.slice(i, i + CHUNK);
    const perTable = new Map<string, typeof slice>();
    for (const item of slice) {
      const list = perTable.get(item.table) ?? [];
      list.push(item);
      perTable.set(item.table, list);
    }
    const changes = [...perTable.entries()].map(([table, list]) => ({
      table,
      rows: list.map((x) => x.row),
    }));

    const result = await api<{ applied: number; rejected: Array<{ table: string; id: string }> }>(
      '/api/sync/push',
      { method: 'POST', body: JSON.stringify({ client_id: cid, changes }) },
    );
    pushed += result.applied;
    rejectedCount += result.rejected.length;

    /* Erfolgreich übertragene (auch verworfene — Server-Stand kommt per
     * Pull) Outbox-Einträge entfernen. Nur die aus DIESEM Häppchen;
     * später eingetroffene Änderungen bleiben stehen. */
    for (const { entry } of slice) {
      const current = await db.sync_outbox.get(entry.key);
      if (current !== undefined && current.modified_at === entry.modified_at) {
        await db.sync_outbox.delete(entry.key);
      }
    }
  }

  return { pushed, rejected: rejectedCount };
}

// ── Pull ───────────────────────────────────────────────────────────────────

interface ServerRow extends Record<string, unknown> {
  rev: number;
  updated_at: string;
  deleted_at: string | null;
}

async function pullChanges(): Promise<number> {
  const since = await getMeta('last_pulled_at');
  const qs = since !== null ? `?since=${encodeURIComponent(since)}` : '';
  const { tables, server_time } = await api<{
    tables: Record<string, ServerRow[]>;
    server_time: string;
  }>(`/api/sync/pull${qs}`);

  let appliedCount = 0;

  for (const [table, rows] of Object.entries(tables)) {
    if (!SYNCED_TABLES.has(table)) continue;
    const pk = pkOf(table);

    await db.transaction('rw', [db.table(table), db.sync_revs, db.sync_outbox], async () => {
      (Dexie.currentTransaction as unknown as { __syncApply: boolean }).__syncApply = true;

      for (const row of rows) {
        const id = row[pk];
        if (typeof id !== 'string') continue;
        const key = `${table}:${id}`;

        /* Lokale ungepushte Änderung vorhanden → lokal gewinnt vorerst;
         * der nächste Push lässt den Server per LWW entscheiden. */
        const pending = await db.sync_outbox.get(key);
        if (pending !== undefined) continue;

        const { rev, updated_at: _u, deleted_at, ...data } = row;
        if (deleted_at !== null) {
          await db.table(table).delete(id);
        } else {
          await db.table(table).put(data);
        }
        await db.sync_revs.put({ key, rev });
        appliedCount++;
      }
    });
  }

  await setMeta('last_pulled_at', server_time);
  return appliedCount;
}

// ── Audit-Log anhängen (append-only) ───────────────────────────────────────

async function pushAudit(): Promise<void> {
  const lastPushed = parseInt((await getMeta('audit_pushed_id')) ?? '0', 10);
  const cid = await clientId();
  const fresh = await db.audit_log.where('id' as never).above(lastPushed).toArray();
  if (fresh.length === 0) return;

  const entries = fresh
    .filter((e) => e.id !== undefined)
    .map((e) => ({
      id: `${cid}:${e.id!}`,
      timestamp: e.timestamp,
      firma: e.firma,
      aktion: e.aktion,
      ...(e.details !== undefined ? { details: e.details } : {}),
      ...(e.user_agent !== undefined ? { user_agent: e.user_agent } : {}),
    }));
  await api('/api/sync/audit', { method: 'POST', body: JSON.stringify({ entries }) });
  const maxId = Math.max(...fresh.map((e) => e.id ?? 0));
  await setMeta('audit_pushed_id', String(maxId));
}

// ── Voll-Push (Erstmigration / nach Restore) ──────────────────────────────

/** Markiert ALLE lokalen Datensätze als dirty → nächster Sync lädt alles hoch. */
export async function queueFullPush(): Promise<number> {
  const now = new Date().toISOString();
  let count = 0;
  for (const table of SYNCED_TABLES) {
    const pk = pkOf(table);
    const rows = (await db.table(table).toArray()) as Array<Record<string, unknown>>;
    const entries: SyncOutboxEntry[] = [];
    for (const row of rows) {
      const id = row[pk];
      if (typeof id !== 'string') continue;
      if (table === 'einstellungen' && !SYNC_SETTINGS_KEYS.has(id)) continue;
      entries.push({ key: `${table}:${id}`, table, row_id: id, deleted: false, modified_at: now });
    }
    if (entries.length > 0) await db.sync_outbox.bulkPut(entries);
    count += entries.length;
  }
  return count;
}

// ── Orchestrierung ─────────────────────────────────────────────────────────

export interface SyncStatus {
  configured: boolean;
  running: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
  pendingCount: number;
  authExpired: boolean;   // Token vorhanden, aber vom Server als ungültig/abgelaufen abgewiesen
}

let running = false;
let lastError: string | null = null;
let authExpired = false;
let debounceTimer: number | null = null;
let intervalTimer: number | null = null;

function emitStatus(): void {
  window.dispatchEvent(new CustomEvent('amed:sync-status'));
}

export async function getSyncStatus(): Promise<SyncStatus> {
  return {
    configured: await isSyncConfigured(),
    running,
    lastSyncAt: await getMeta('last_sync_at'),
    lastError,
    pendingCount: await db.sync_outbox.count(),
    authExpired,
  };
}

export async function syncNow(): Promise<void> {
  if (running) return;
  if (!navigator.onLine) return;
  if (!(await isSyncConfigured())) return;

  running = true;
  emitStatus();
  try {
    await pushOutbox();
    await pullChanges();
    await pushAudit().catch(() => { /* Audit-Push ist Best-Effort */ });
    await setMeta('last_sync_at', new Date().toISOString());
    lastError = null;
  } catch (e) {
    lastError = e instanceof Error ? e.message : String(e);
    console.error('[Sync] Fehler:', e);
  } finally {
    running = false;
    emitStatus();
  }
}

function scheduleDebouncedSync(): void {
  if (debounceTimer !== null) clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(() => {
    debounceTimer = null;
    void syncNow();
  }, WRITE_DEBOUNCE_MS);
}

/** Nach dem Boot aufrufen: startet Intervall + Reconnect-Trigger. */
export function startSyncLoop(): void {
  if (intervalTimer !== null) return;
  intervalTimer = window.setInterval(() => { void syncNow(); }, SYNC_INTERVAL_MS);
  window.addEventListener('online', () => { void syncNow(); });
  void syncNow();
}
